import axios from 'axios';

// En desarrollo usa localhost directo.
// En producción (build) usa el mismo origen del navegador → el proxy de app.js reenvía /api al backend.
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined'
    ? `${window.location.origin}/api`
    : 'http://localhost:8000/api');

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Las dos identidades guardan su token por separado, de modo que un tecnico y un
// usuario del gestor pueden convivir en el mismo navegador sin pisarse la sesion.
export const TECHNICIAN_AREA = '/tecnicos';
export const isTechnicianArea = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith(TECHNICIAN_AREA);

// Interceptor para agregar token
api.interceptors.request.use((config) => {
  const key = isTechnicianArea() ? 'technician_token' : 'token';
  const token = localStorage.getItem(key);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Si el token caduca (dura 8 horas), se vuelve al login en lugar de dejar la
// aplicacion con datos viejos en pantalla y cada accion nueva fallando en
// silencio. Vale para las dos identidades, cada una con su propia sesion.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    // Un 401 en el propio login es "credenciales incorrectas", no sesion caducada
    const isLoginCall = url.includes('/auth/login') || url.includes('/auth/technician/login');

    if (status === 401 && !isLoginCall) {
      const key = isTechnicianArea() ? 'technician_token' : 'token';
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        if (key === 'token') localStorage.removeItem('user');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  },
);

// --- Sesion larga de la app movil ---
// El token de la app dura 90 dias y el servidor lo renueva con /auth/refresh. Aqui
// se llama al abrir la app y al volver a ella, si el token tiene mas de un dia: asi
// quien la usa a diario no vuelve a ver el login, y quien la deja 90 dias sin abrir
// si. Los tokens normales del escritorio (8 h) no llevan `rem` y no se tocan.
const RENEW_AFTER_SECONDS = 24 * 3600;

const tokenClaims = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload + '='.repeat((4 - (payload.length % 4)) % 4)));
  } catch (e) {
    return null;
  }
};

// Se renuevan las dos sesiones (gestor y tecnico), sin depender de la ruta en la que
// se este: la app instalada arranca en /tecnicos y manda al gestor a /chats, y mirar
// solo la clave de la ruta actual dejaria sin renovar la sesion de quien entra asi.
// Va con axios suelto, no con `api`, porque el interceptor de `api` elige el token
// por la ruta y pisaria el de la sesion que se esta renovando.
async function renewKey(key) {
  const token = localStorage.getItem(key);
  const claims = token && tokenClaims(token);
  if (!claims?.rem || Date.now() / 1000 - (claims.iat || 0) < RENEW_AFTER_SECONDS) return;
  try {
    const res = await axios.post(`${API_BASE}/auth/refresh`, null, {
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.setItem(key, res.data.access_token);
  } catch (e) {
    // Si el servidor responde 401 la sesion ya no vale (acceso revocado, usuario
    // desactivado o contrasena cambiada): se retira y la proxima carga pide login.
    // Un fallo de red se reintenta la proxima vez que se abra la app.
    if (e.response?.status === 401) {
      localStorage.removeItem(key);
      if (key === 'token') localStorage.removeItem('user');
    }
  }
}

export const renewSession = () => Promise.all([renewKey('token'), renewKey('technician_token')]);

let renewalStarted = false;
export function startSessionRenewal() {
  if (renewalStarted || typeof document === 'undefined') return;
  renewalStarted = true;
  renewSession();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') renewSession();
  });
}

// Login del gestor desde la app movil (/tecnicos): misma cuenta y token que el
// escritorio, pero con sesion larga. Guarda lo mismo que AuthContext.login.
export async function managerLogin(email, password) {
  const res = await api.post('/auth/login', { email, password, remember: true });
  localStorage.setItem('token', res.data.access_token);
  localStorage.setItem('user', JSON.stringify(res.data.user));
  return res.data.user;
}

// --- Clientes ---
export const clientsAPI = {
  list: (q) => api.get('/clients/', { params: q ? { q } : {} }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients/', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// --- Proyectos ---
export const projectsAPI = {
  list: (params) => api.get('/projects/', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects/', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  createFolders: (id) => api.post(`/projects/${id}/create-folders`),
  createOfferFolders: (id) => api.post(`/projects/${id}/create-offer-folders`),
  linkDriveFolder: (id, folderId, kind) =>
    api.post(`/projects/${id}/link-drive-folder`, { folder_id: folderId, kind }),
  unlinkDriveFolder: (id, kind) =>
    api.post(`/projects/${id}/unlink-drive-folder`, null, { params: { kind } }),
};

// --- Ingenieria (datos generales por fase) ---
export const engineeringAPI = {
  getConceptual: (projectId) => api.get(`/engineering/conceptual/${projectId}`),
  createConceptual: (data) => api.post('/engineering/conceptual', data),
  updateConceptual: (projectId, data) => api.put(`/engineering/conceptual/${projectId}`, data),
  getBasic: (projectId) => api.get(`/engineering/basic/${projectId}`),
  createBasic: (data) => api.post('/engineering/basic', data),
  updateBasic: (projectId, data) => api.put(`/engineering/basic/${projectId}`, data),
  getDetail: (projectId) => api.get(`/engineering/detail/${projectId}`),
  createDetail: (data) => api.post('/engineering/detail', data),
  updateDetail: (projectId, data) => api.put(`/engineering/detail/${projectId}`, data),
  // Phase management
  advancePhase: (projectId) => api.post(`/engineering/projects/${projectId}/advance-phase`),
  rollbackPhase: (projectId) => api.post(`/engineering/projects/${projectId}/rollback-phase`),
  // Copy conditions
  copyClientConditions: (projectId) => api.post(`/engineering/projects/${projectId}/copy-client-conditions`),
  copySupplierConditions: (projectId, supplierId) => api.post(`/engineering/projects/${projectId}/copy-supplier-conditions/${supplierId}`),
  refreshSupplierConditions: (projectId) => api.post(`/engineering/projects/${projectId}/refresh-supplier-conditions`),
};

// --- Materias Primas ---
export const rawMaterialsAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/raw-materials`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/raw-materials`, data),
  update: (id, data) => api.put(`/engineering/raw-materials/${id}`, data),
  delete: (id) => api.delete(`/engineering/raw-materials/${id}`),
};

// --- Etapas del Proceso ---
export const processStagesAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/process-stages`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/process-stages`, data),
  update: (id, data) => api.put(`/engineering/process-stages/${id}`, data),
  delete: (id) => api.delete(`/engineering/process-stages/${id}`),
};

// --- Utilities ---
export const utilitiesAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/utilities`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/utilities`, data),
  update: (id, data) => api.put(`/engineering/utilities/${id}`, data),
  delete: (id) => api.delete(`/engineering/utilities/${id}`),
};

// --- Formatos de Envasado ---
export const packagingSpecsAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/packaging-specs`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/packaging-specs`, data),
  update: (id, data) => api.put(`/engineering/packaging-specs/${id}`, data),
  delete: (id) => api.delete(`/engineering/packaging-specs/${id}`),
};

// --- Equipos ---
export const equipmentsAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/equipments`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/equipments`, data),
  update: (id, data) => api.put(`/engineering/equipments/${id}`, data),
  delete: (id) => api.delete(`/engineering/equipments/${id}`),
};

// --- Tareas de Ingenieria ---
export const engineeringTasksAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/engineering-tasks`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/engineering-tasks`, data),
  update: (id, data) => api.put(`/engineering/engineering-tasks/${id}`, data),
  delete: (id) => api.delete(`/engineering/engineering-tasks/${id}`),
};

// --- Maquinas (Ingenieria Conceptual) ---
export const machinesAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/machines`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/machines`, data),
  update: (id, data) => api.put(`/engineering/machines/${id}`, data),
  delete: (id) => api.delete(`/engineering/machines/${id}`),
};

// --- Ofertas de Proveedores ---
export const supplierOffersAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/supplier-offers`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/supplier-offers`, data),
  update: (id, data) => api.put(`/engineering/supplier-offers/${id}`, data),
  delete: (id) => api.delete(`/engineering/supplier-offers/${id}`),
  // Condiciones de pago estructuradas de oferta
  listPaymentConditions: (offerId) => api.get(`/engineering/supplier-offers/${offerId}/payment-conditions`),
  createPaymentCondition: (offerId, data) => api.post(`/engineering/supplier-offers/${offerId}/payment-conditions`, data),
  updatePaymentCondition: (id, data) => api.put(`/engineering/supplier-offer-payment-conditions/${id}`, data),
  deletePaymentCondition: (id) => api.delete(`/engineering/supplier-offer-payment-conditions/${id}`),
  copySupplierDefaults: (offerId) => api.post(`/engineering/supplier-offers/${offerId}/copy-supplier-defaults`),
  // Licitacion: partidas de la oferta
  listLines: (offerId) => api.get(`/engineering/supplier-offers/${offerId}/lines`),
  createLine: (offerId, data) => api.post(`/engineering/supplier-offers/${offerId}/lines`, data),
  updateLine: (lineId, data) => api.put(`/engineering/offer-lines/${lineId}`, data),
  deleteLine: (lineId) => api.delete(`/engineering/offer-lines/${lineId}`),
  promoteLine: (lineId) => api.post(`/engineering/offer-lines/${lineId}/promote-to-equipment`),
  splitGroup: (offerId, data) => api.post(`/engineering/supplier-offers/${offerId}/split-group`, data),
  revise: (offerId) => api.post(`/engineering/supplier-offers/${offerId}/revise`),
  requestDocument: (offerId) => api.get(`/engineering/supplier-offers/${offerId}/request-document`, { responseType: 'blob' }),
  // Adjudicacion y pedido
  awardLine: (lineId, awarded) => api.post(`/engineering/offer-lines/${lineId}/award`, { awarded }),
  awardAll: (offerId) => api.post(`/engineering/supplier-offers/${offerId}/award-all`),
  createOrder: (offerId, data = {}) => api.post(`/engineering/supplier-offers/${offerId}/create-order`, data),
};

// --- Licitacion: peticion de ofertas desde la lista de equipos ---
export const rfqAPI = {
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/rfq`, data),
  comparison: (projectId, rfqCode) => api.get(`/engineering/projects/${projectId}/quote-comparison`, {
    params: rfqCode ? { rfq_code: rfqCode } : {},
  }),
};

// --- Partidas de coste por proyecto ---
export const costEntriesAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/cost-entries`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/cost-entries`, data),
  update: (id, data) => api.put(`/engineering/cost-entries/${id}`, data),
  delete: (id) => api.delete(`/engineering/cost-entries/${id}`),
};

// --- Pedidos de Compra ---
export const ordersAPI = {
  list: (projectId) => api.get(`/orders/projects/${projectId}`),
  offersWithoutOrder: (projectId) => api.get(`/orders/projects/${projectId}/offers-without-order`),
  create: (projectId, data) => api.post(`/orders/projects/${projectId}`, data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  // El endpoint exige autenticacion; window.open(url) no manda la cabecera Authorization,
  // asi que el PDF se descarga como blob (con el token via el interceptor de axios) y se
  // abre desde un object URL local.
  pdfBlob: (id, lang = 'es') => api.get(`/orders/${id}/pdf`, { params: { lang }, responseType: 'blob' }),
  // Condiciones de pago estructuradas del pedido
  listPaymentConditions: (orderId) => api.get(`/orders/${orderId}/payment-conditions`),
  createPaymentCondition: (orderId, data) => api.post(`/orders/${orderId}/payment-conditions`, data),
  updatePaymentCondition: (id, data) => api.put(`/orders/payment-conditions/${id}`, data),
  deletePaymentCondition: (id) => api.delete(`/orders/payment-conditions/${id}`),
};

// --- Ordenes de Fabricacion ---
export const manufacturingAPI = {
  list: (projectId) => api.get(`/manufacturing/projects/${projectId}/orders`),
  nextCode: (projectId) => api.get(`/manufacturing/projects/${projectId}/next-code`),
  create: (projectId, data) => api.post(`/manufacturing/projects/${projectId}/orders`, data),
  update: (id, data) => api.put(`/manufacturing/orders/${id}`, data),
  delete: (id) => api.delete(`/manufacturing/orders/${id}`),
  assignableOffers: (projectId) => api.get(`/manufacturing/projects/${projectId}/assignable-offers`),
  assignOffers: (id, offerIds) => api.post(`/manufacturing/orders/${id}/offers`, { offer_ids: offerIds }),
  unassignOffer: (id, offerId) => api.delete(`/manufacturing/orders/${id}/offers/${offerId}`),
};

// --- Condiciones de cobro del proyecto (cliente) ---
export const projectClientConditionsAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/client-conditions`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/client-conditions`, data),
  update: (id, data) => api.put(`/engineering/client-conditions/${id}`, data),
  delete: (id) => api.delete(`/engineering/client-conditions/${id}`),
};

// --- Condiciones de pago del proyecto (proveedores) ---
export const projectSupplierConditionsAPI = {
  list: (projectId) => api.get(`/engineering/projects/${projectId}/supplier-conditions`),
  create: (projectId, data) => api.post(`/engineering/projects/${projectId}/supplier-conditions`, data),
  update: (id, data) => api.put(`/engineering/supplier-conditions/${id}`, data),
  delete: (id) => api.delete(`/engineering/supplier-conditions/${id}`),
};

// --- Documentos ---
export const documentsAPI = {
  listFolders: (projectId) => api.get(`/documents/folders/${projectId}`),
  list: (projectId, folderId) => api.get(`/documents/${projectId}`, { params: { folder_id: folderId } }),
  upload: (projectId, formData) => api.post(`/documents/upload/${projectId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/documents/${id}`),
  // Archiva la oferta en <proyecto>/INGENIERIA/OFERTA PROVEEDORES/<proveedor>
  uploadSupplierOffer: (projectId, supplierName, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/documents/projects/${projectId}/supplier-offer`, form, {
      params: { supplier_name: supplierName || '' },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// --- Navegador de ficheros ---
export const filesAPI = {
  browse: (path) => api.get('/documents/fs/browse', { params: path ? { path } : {} }),
  // El endpoint exige autenticacion, asi que el contenido se descarga como blob
  // (una <img src> o un <iframe src> no enviarian la cabecera Authorization).
  fetchBlob: (path) => api.get('/documents/fs/serve', {
    params: { path },
    responseType: 'blob',
  }),
  upload: (path, formData) => api.post('/documents/fs/upload', formData, {
    params: { path },
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  mkdir: (path, name) => api.post('/documents/fs/mkdir', null, {
    params: path ? { path, name } : { name },
  }),
  roots: () => api.get('/documents/fs/roots'),
};

// --- Google Drive (OAuth por usuario) ---
export const googleAPI = {
  status: () => api.get('/auth/google/status'),
  start: () => api.get('/auth/google/start'),
  disconnect: () => api.post('/auth/google/disconnect'),
};

// --- Economico ---
export const economicAPI = {
  getBudget: (projectId) => api.get(`/economic/budget/${projectId}`),
  createBudget: (data) => api.post('/economic/budget', data),
  listBudgetItems: (budgetId) => api.get(`/economic/budget/${budgetId}/items`),
  createBudgetItem: (data) => api.post('/economic/budget/items', data),
  listMilestones: (projectId) => api.get(`/economic/milestones/${projectId}`),
  createMilestone: (data) => api.post('/economic/milestones', data),
  updateMilestone: (id, data) => api.put(`/economic/milestones/${id}`, data),
  listCashFlows: (projectId) => api.get(`/economic/cashflow/${projectId}`),
  createCashFlow: (data) => api.post('/economic/cashflow', data),
  listPaymentConditions: (projectId) => api.get(`/economic/payment-conditions/${projectId}`),
  createPaymentCondition: (data) => api.post('/economic/payment-conditions', data),
};

// --- Proveedores ---
export const suppliersAPI = {
  list: (q) => api.get('/suppliers/', { params: q ? { q } : {} }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers/', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  listConditions: (supplierId) => api.get(`/suppliers/${supplierId}/conditions`),
  createCondition: (supplierId, data) => api.post(`/suppliers/${supplierId}/conditions`, data),
  updateCondition: (conditionId, data) => api.put(`/suppliers/conditions/${conditionId}`, data),
  deleteCondition: (conditionId) => api.delete(`/suppliers/conditions/${conditionId}`),
};

// --- Condiciones por defecto del cliente ---
export const clientConditionsAPI = {
  list: (clientId) => api.get(`/clients/${clientId}/conditions`),
  create: (clientId, data) => api.post(`/clients/${clientId}/conditions`, data),
  update: (conditionId, data) => api.put(`/clients/conditions/${conditionId}`, data),
  delete: (conditionId) => api.delete(`/clients/conditions/${conditionId}`),
};

// --- Usuarios (admin) ---
export const usersAPI = {
  list: () => api.get('/auth/users'),
  create: (data) => api.post('/auth/users', data),
  update: (id, data) => api.put(`/auth/users/${id}`, data),
  delete: (id) => api.delete(`/auth/users/${id}`),
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/me', { password: newPassword, current_password: currentPassword }),
};

// --- Backups (admin) ---
export const backupAPI = {
  list: () => api.get('/backup/'),
  create: () => api.post('/backup/'),
  delete: (filename) => api.delete(`/backup/${encodeURIComponent(filename)}`),
  restore: (filename) => api.post(`/backup/${encodeURIComponent(filename)}/restore`),
  restoreStatus: (jobId) => api.get(`/backup/restore-status/${jobId}`),
  download: (filename) => api.get(`/backup/${encodeURIComponent(filename)}/download`, { responseType: 'blob' }),
};

// --- Ofertas de proyecto ---
export const offersAPI = {
  list: (projectId) => api.get(`/offers/projects/${projectId}`),
  create: (projectId, data) => api.post(`/offers/projects/${projectId}`, data),
  update: (offerId, data) => api.put(`/offers/${offerId}`, data),
  delete: (offerId) => api.delete(`/offers/${offerId}`),
  approve: (offerId) => api.post(`/offers/${offerId}/approve`),
  unapprove: (offerId) => api.post(`/offers/${offerId}/unapprove`),
  // Equipos de la oferta
  listEquipments: (offerId) => api.get(`/offers/${offerId}/equipments`),
  createEquipment: (offerId, data) => api.post(`/offers/${offerId}/equipments`, data),
  updateEquipment: (id, data) => api.put(`/offers/equipments/${id}`, data),
  deleteEquipment: (id) => api.delete(`/offers/equipments/${id}`),
  // Partidas de coste de la oferta
  listCostEntries: (offerId) => api.get(`/offers/${offerId}/cost-entries`),
  createCostEntry: (offerId, data) => api.post(`/offers/${offerId}/cost-entries`, data),
  updateCostEntry: (id, data) => api.put(`/offers/cost-entries/${id}`, data),
  deleteCostEntry: (id) => api.delete(`/offers/cost-entries/${id}`),
};

// --- Comunicaciones ---
export const communicationsAPI = {
  list: (projectId) => api.get(`/communications/projects/${projectId}`),
  listOverduePending: () => api.get('/communications/overdue-pending'),
  create: (projectId, data) => api.post(`/communications/projects/${projectId}`, data),
  update: (id, data) => api.put(`/communications/${id}`, data),
  delete: (id) => api.delete(`/communications/${id}`),
};

// --- Planning ---
export const planningAPI = {
  list: (projectId) => api.get(`/planning/projects/${projectId}`),
  create: (projectId, data) => api.post(`/planning/projects/${projectId}`, data),
  update: (id, data) => api.put(`/planning/${id}`, data),
  delete: (id) => api.delete(`/planning/${id}`),
  sync: (projectId) => api.post(`/planning/projects/${projectId}/sync`),
};

// --- Tecnicos (repertorio fijo) ---
export const techniciansAPI = {
  list: (params) => api.get('/technicians/', { params }),
  get: (id) => api.get(`/technicians/${id}`),
  create: (data) => api.post('/technicians/', data),
  update: (id, data) => api.put(`/technicians/${id}`, data),
  delete: (id) => api.delete(`/technicians/${id}`),
  summary: (id, params) => api.get(`/technicians/${id}/summary`, { params }),
};

// --- Calendario de asignaciones de tecnicos ---
export const technicianAssignmentsAPI = {
  list: (params) => api.get('/technicians/assignments', { params }),
  create: (data) => api.post('/technicians/assignments', data),
  update: (id, data) => api.put(`/technicians/assignments/${id}`, data),
  delete: (id) => api.delete(`/technicians/assignments/${id}`),
  calendar: (start, end, projectId) =>
    api.get('/technicians/calendar', { params: { start, end, ...(projectId ? { project_id: projectId } : {}) } }),
  workload: (start, end) => api.get('/technicians/workload', { params: { start, end } }),
};

// --- Tareas de campo ---
// Unico modulo compartido por las dos identidades: usuarios del gestor y tecnicos.
export const tasksAPI = {
  list: (params) => api.get('/tasks/', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks/', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  my: () => api.get('/tasks/my'),
  board: (params) => api.get('/tasks/board', { params }),
  unassignedOrder: () => api.get('/tasks/unassigned-order'),
  openOrders: () => api.get('/tasks/open-orders'),
  assignableTechnicians: (manufacturingOrderId) =>
    api.get('/tasks/assignable-technicians', {
      params: manufacturingOrderId ? { manufacturing_order_id: manufacturingOrderId } : {},
    }),
  assign: (id, data) => api.patch(`/tasks/${id}/assign`, data),
  setStatus: (id, data) => api.patch(`/tasks/${id}/status`, data),
  setManufacturingOrder: (id, manufacturingOrderId) =>
    api.patch(`/tasks/${id}/manufacturing-order`, { manufacturing_order_id: manufacturingOrderId }),
  addUpdate: (id, data) => api.post(`/tasks/${id}/updates`, data),
  listUpdates: (id) => api.get(`/tasks/${id}/updates`),
  deleteUpdatePhoto: (id, updateId) => api.delete(`/tasks/${id}/updates/${updateId}/photo`),
  addChecklistItem: (id, text) => api.post(`/tasks/${id}/checklist`, { text }),
  toggleChecklistItem: (itemId, isDone) =>
    api.patch(`/tasks/checklist/${itemId}`, isDone === undefined ? {} : { is_done: isDone }),
  deleteChecklistItem: (itemId) => api.delete(`/tasks/checklist/${itemId}`),
  generateFromOrder: (orderId) => api.post(`/tasks/manufacturing-orders/${orderId}/generate`, {}),
  uploadPhoto: (id, formData) => api.post(`/tasks/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // La foto exige token, asi que se descarga como blob y se pinta desde object URL
  photoBlob: (filename) => api.get(`/tasks/photos/${filename}`, { responseType: 'blob' }),
  // Fotos que siguen en el servidor, pendientes de pasar a Drive
  pendingPhotos: (projectId) => api.get('/tasks/photos/pending', {
    params: projectId ? { project_id: projectId } : {},
  }),
  // Sin target_folder_id van a multimedia/<OF> dentro de la carpeta del proyecto;
  // con el, a la carpeta que el gestor haya elegido a mano en el explorador
  pushPhotosToDrive: (updateIds, options = {}) =>
    api.post('/tasks/photos/push-to-drive', {
      update_ids: updateIds,
      target_folder_id: options.targetFolderId || null,
      link_to_project: !!options.linkToProject,
    }),
  plannedVsActual: (params) => api.get('/tasks/reports/planned-vs-actual', { params }),
};

// --- Documentos HTML entregados a los tecnicos ---
// Cuelgan de /tasks porque es el unico modulo que alcanzan las dos identidades:
// el gestor los sube y los asigna, el tecnico solo ve los suyos.
export const htmlDocsAPI = {
  list: () => api.get('/tasks/html-docs/'),
  get: (id) => api.get(`/tasks/html-docs/${id}`),
  create: (formData) => api.post('/tasks/html-docs/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, data) => api.put(`/tasks/html-docs/${id}`, data),
  assign: (id, data) => api.post(`/tasks/html-docs/${id}/assign`, data),
  replaceFile: (id, formData) => api.post(`/tasks/html-docs/${id}/file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/tasks/html-docs/${id}`),
  // El contenido exige token, asi que se descarga como texto y se pinta en un
  // iframe con srcDoc, en sandbox y sin acceso al origen de la aplicacion.
  content: (id) => api.get(`/tasks/html-docs/${id}/content`, { responseType: 'text' }),
};

// --- Plantillas de tareas (alimentan "Generar tareas" de la OF) ---
export const taskTemplatesAPI = {
  list: (projectType) => api.get('/tasks/templates/', {
    params: projectType ? { project_type: projectType } : {},
  }),
  create: (data) => api.post('/tasks/templates/', data),
  update: (id, data) => api.put(`/tasks/templates/${id}`, data),
  delete: (id) => api.delete(`/tasks/templates/${id}`),
  loadDefaults: () => api.post('/tasks/templates/load-defaults'),
};

// --- Acceso de tecnicos a la app (gestion desde la ficha, requiere usuario) ---
export const technicianAccessAPI = {
  get: (id) => api.get(`/technicians/${id}/access`),
  grant: (id, data) => api.post(`/technicians/${id}/access`, data || {}),
  update: (id, data) => api.put(`/technicians/${id}/access`, data),
  revoke: (id) => api.delete(`/technicians/${id}/access`),
  // El QR exige token, asi que se descarga como blob y se pinta desde un object URL
  qrBlob: (id) => api.get(`/technicians/${id}/access/qr`, { responseType: 'blob' }),
};

// --- Chat de proyecto ---
// Lo usan las dos identidades: la app movil y la pestana Chat del proyecto.
export const chatAPI = {
  conversations: () => api.get('/chat/conversations'),
  unread: () => api.get('/chat/unread'),
  messages: (projectId, params) => api.get(`/chat/projects/${projectId}/messages`, { params }),
  send: (projectId, body) => api.post(`/chat/projects/${projectId}/messages`, { body }),
  upload: (projectId, formData, onUploadProgress) =>
    api.post(`/chat/projects/${projectId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  markRead: (projectId, lastMessageId) =>
    api.post(`/chat/projects/${projectId}/read`, { last_message_id: lastMessageId }),
  delete: (messageId) => api.delete(`/chat/messages/${messageId}`),
  // Los adjuntos exigen token: se descargan como blob y se pintan desde un object URL
  fileBlob: (filename) => api.get(`/chat/files/${filename}`, { responseType: 'blob' }),
  pushKey: () => api.get('/chat/push/key'),
  pushSubscribe: (subscription) => api.post('/chat/push/subscribe', subscription),
  pushUnsubscribe: (endpoint) => api.post('/chat/push/unsubscribe', { endpoint }),
  pushTest: () => api.post('/chat/push/test'),
};

// --- Autenticacion de tecnicos (identidad separada de los usuarios) ---
export const technicianAuthAPI = {
  login: (loginCode, pin) => api.post('/auth/technician/login', { login_code: loginCode, pin }),
  me: () => api.get('/auth/technician/me'),
  changePin: (currentPin, newPin) =>
    api.post('/auth/technician/change-pin', { current_pin: currentPin, new_pin: newPin }),
};

// --- Configuracion ---
export const configAPI = {
  // Project Types
  listProjectTypes: () => api.get('/config/project-types'),
  createProjectType: (data) => api.post('/config/project-types', data),
  updateProjectType: (id, data) => api.put(`/config/project-types/${id}`, data),
  deleteProjectType: (id) => api.delete(`/config/project-types/${id}`),
  // Container Types
  listContainerTypes: () => api.get('/config/container-types'),
  createContainerType: (data) => api.post('/config/container-types', data),
  updateContainerType: (id, data) => api.put(`/config/container-types/${id}`, data),
  deleteContainerType: (id) => api.delete(`/config/container-types/${id}`),
  // Cost Partidas
  listCostPartidas: (projectType) => api.get('/config/cost-partidas', { params: projectType ? { project_type: projectType } : {} }),
  createCostPartida: (data) => api.post('/config/cost-partidas', data),
  updateCostPartida: (id, data) => api.put(`/config/cost-partidas/${id}`, data),
  deleteCostPartida: (id) => api.delete(`/config/cost-partidas/${id}`),
  // Disciplines
  listDisciplines: () => api.get('/config/disciplines'),
  createDiscipline: (data) => api.post('/config/disciplines', data),
  updateDiscipline: (id, data) => api.put(`/config/disciplines/${id}`, data),
  deleteDiscipline: (id) => api.delete(`/config/disciplines/${id}`),
  // Role Permissions
  listRolePermissions: () => api.get('/config/role-permissions'),
  createRolePermissions: (data) => api.post('/config/role-permissions', data),
  updateRolePermissions: (role, permissions, label) => api.put(`/config/role-permissions/${role}`, { permissions, ...(label ? { label } : {}) }),
  deleteRolePermissions: (role) => api.delete(`/config/role-permissions/${role}`),
  // Document / General Settings
  getSettings: () => api.get('/config/settings'),
  updateSetting: (key, value) => api.put(`/config/settings/${key}`, { value }),
};

// --- Herramientas ---
export const toolsAPI = {
  list: () => api.get('/tools/'),
  get: (id) => api.get(`/tools/${id}`),
  create: (data) => api.post('/tools/', data),
  update: (id, data) => api.put(`/tools/${id}`, data),
  delete: (id) => api.delete(`/tools/${id}`),
};

export default api;
